import React from 'react';
import { MessageCircle, Mail, Clock, Globe, Smartphone, Users, Send, ChevronRight, Zap } from 'lucide-react';

const VisionTechContactPage = () => {
  // Contact Form State and Handlers
  const [formData, setFormData] = React.useState({
    name: '',
    email: '',
    phone: '',
    department: 'general',
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [formMessage, setFormMessage] = React.useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setFormMessage({
        type: 'success',
        text: 'Message received! Our tech specialists will analyze your inquiry and respond within 24 hours.'
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        department: 'general',
        message: ''
      });
    }, 1500);
  };

  // Contact Methods
  const handleEmailClick = (email) => {
    window.location.href = `mailto:${email}`;
  };

  const handleTelegramClick = () => {
    window.open("https://t.me/VisionTech2030SUPPORTTEAM", "_blank");
  };

  return (
    <div className="vision-tech-contact-page">
      <div className="vision-tech-contact-header">
        <div className="vision-tech-logo-container">
          <div className="vision-tech-logo">
            <Zap size={28} className="vision-tech-logo-icon" />
            <div className="vision-tech-logo-pulse"></div>
          </div>
        </div>
        <h1>Connect with <span>Vision Tech 2030</span></h1>
        <p className="vision-tech-header-text">
          Let's collaborate to create the future of investment in Tech. Our innovation team is ready to discuss your technological needs and solutions.
        </p>
      </div>
      
      <div className="vision-tech-contact-grid">
        <div className="vision-tech-form-card">
          <div className="vision-tech-card-inner">
            <h2>Send a Message</h2>
            <div className="vision-tech-divider"></div>
            
            {formMessage && (
              <div className={`vision-tech-form-message vision-tech-${formMessage.type}`}>
                {formMessage.text}
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="vision-tech-form">
              <div className="vision-tech-form-row">
                <div className="vision-tech-form-group">
                  <label htmlFor="name">Full Name</label>
                  <div className="vision-tech-input-container">
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Full Name"
                      required
                    />
                  </div>
                </div>
                
                <div className="vision-tech-form-group">
                  <label htmlFor="email">Email Address</label>
                  <div className="vision-tech-input-container">
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Email"
                      required
                    />
                  </div>
                </div>
              </div>
              
              <div className="vision-tech-form-row">
                <div className="vision-tech-form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <div className="vision-tech-input-container">
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="Mobile"
                    />
                  </div>
                </div>
                
                <div className="vision-tech-form-group">
                  <label htmlFor="department">Department</label>
                  <div className="vision-tech-input-container">
                    <select
                      id="department"
                      name="department"
                      value={formData.department}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="general">General Inquiry</option>
                      <option value="sales">Sales & Partnerships</option>
                      <option value="support">Technical Support</option>
                      <option value="careers">Careers</option>
                      <option value="innovation">Investments</option>
                    </select>
                  </div>
                </div>
              </div>
              
              <div className="vision-tech-form-group">
                <label htmlFor="message">Your Message</label>
                <div className="vision-tech-input-container vision-tech-textarea-container">
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Tell us about your project or inquiry..."
                    rows="4"
                    required
                  ></textarea>
                </div>
              </div>
              
              <button
                type="submit"
                className="vision-tech-submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <div className="vision-tech-loading-spinner">
                    <div className="vision-tech-spinner-ring"></div>
                  </div>
                ) : (
                  <>
                    <span>Send Message</span>
                    <ChevronRight size={18} />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
        
        <div className="vision-tech-contact-options">
          <div className="vision-tech-contact-card">
            <div className="vision-tech-contact-card-header">
              <MessageCircle size={22} />
              <h3>Instant Support</h3>
            </div>
            <p>Get immediate assistance with our 24/7 technical support team</p>
            <button className="vision-tech-action-btn" onClick={handleTelegramClick}>
              <Send size={16} />
              <span>Contact Support</span>
            </button>
          </div>
          
          <div className="vision-tech-contact-card">
            <div className="vision-tech-contact-card-header">
              <Mail size={22} />
              <h3>Email Us</h3>
            </div>
            <p>Reach out to our specialized departments directly</p>
            <div className="vision-tech-email-links">
              <button 
                className="vision-tech-email-link" 
                onClick={() => handleEmailClick('info@visiontech2030.com')}
              >
                <span>General Inquiries</span>
                <span className="vision-tech-email-address">info@visiontech2030.com</span>
              </button>
              <button 
                className="vision-tech-email-link" 
                onClick={() => handleEmailClick('partners@visiontech2030.com')}
              >
                <span>Partnerships</span>
                <span className="vision-tech-email-address">partners@visiontech2030.com</span>
              </button>
              <button 
                className="vision-tech-email-link" 
                onClick={() => handleEmailClick('careers@visiontech2030.com')}
              >
                <span>Join Our Team</span>
                <span className="vision-tech-email-address">careers@visiontech2030.com</span>
              </button>
            </div>
          </div>
          
          <div className="vision-tech-contact-card">
            <div className="vision-tech-contact-card-header">
              <Clock size={22} />
              <h3>Hours of Operation</h3>
            </div>
            <div className="vision-tech-hours">
              <div className="vision-tech-hours-item">
                <span className="vision-tech-hours-day">Technical Support:</span>
                <span className="vision-tech-hours-time">24/7/365</span>
              </div>
              <div className="vision-tech-hours-item">
                <span className="vision-tech-hours-day">Business Inquiries:</span>
                <span className="vision-tech-hours-time">Mon-Fri, 8am-7pm</span>
              </div>
              <div className="vision-tech-hours-item">
                <span className="vision-tech-hours-day">Innovation Lab:</span>
                <span className="vision-tech-hours-time">Mon-Fri, 9am-6pm</span>
              </div>
            </div>
          </div>
          
          <div className="vision-tech-contact-card">
            <div className="vision-tech-contact-card-header">
              <Users size={22} />
              <h3>Schedule a Consultation</h3>
            </div>
            <p>Book a personalized meeting with our technology specialists</p>
            <button className="vision-tech-action-btn vision-tech-calendar-btn">
              <span>Book Appointment</span>
            </button>
          </div>
          
          <div className="vision-tech-contact-card">
            <div className="vision-tech-contact-card-header">
              <Globe size={22} />
              <h3>Global Presence</h3>
            </div>
            <p>Vision Tech 2030 innovation hubs around the world</p>
            <div className="vision-tech-locations">
              <span className="vision-tech-location">San Francisco</span>
              <span className="vision-tech-location">New York</span>
              <span className="vision-tech-location">London</span>
              <span className="vision-tech-location">Berlin</span>
              <span className="vision-tech-location">Singapore</span>
              <span className="vision-tech-location">Tokyo</span>
              <span className="vision-tech-location">Sydney</span>
              <span className="vision-tech-location">Dubai</span>
            </div>
          </div>
        </div>
      </div>
      
      <style jsx>{`
        .vision-tech-contact-page {
          font-family: 'Inter', 'Segoe UI', Roboto, -apple-system, sans-serif;
          background: #f9fafb;
          color: #1f2937;
          max-width: 1400px;
          margin: 0 auto;
          padding: 3rem 1.5rem;
          background-image: 
            radial-gradient(circle at 20px 30px, rgba(79, 70, 229, 0.03) 0%, transparent 50%),
            radial-gradient(circle at 90% 80%, rgba(16, 185, 129, 0.03) 0%, transparent 50%);
          position: relative;
          overflow: hidden;
        }
        
        .vision-tech-contact-page::before {
          content: '';
          position: absolute;
          width: 300px;
          height: 300px;
          background: linear-gradient(45deg, rgba(79, 70, 229, 0.03), rgba(16, 185, 129, 0.03));
          border-radius: 50%;
          top: -150px;
          right: -150px;
          z-index: -1;
        }
        
        .vision-tech-contact-page::after {
          content: '';
          position: absolute;
          width: 250px;
          height: 250px;
          background: linear-gradient(45deg, rgba(16, 185, 129, 0.03), rgba(79, 70, 229, 0.03));
          border-radius: 50%;
          bottom: -100px;
          left: -100px;
          z-index: -1;
        }
        
        .vision-tech-contact-header {
          text-align: center;
          margin-bottom: 3.5rem;
          position: relative;
        }
        
        .vision-tech-logo-container {
          margin-bottom: 1.5rem;
        }
        
        .vision-tech-logo {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 64px;
          height: 64px;
          border-radius: 16px;
          background: linear-gradient(135deg, #4f46e5, #7c3aed);
          position: relative;
          margin: 0 auto;
        }
        
        .vision-tech-logo-icon {
          color: white;
        }
        
        .vision-tech-logo-pulse {
          position: absolute;
          width: 100%;
          height: 100%;
          border-radius: 16px;
          background: linear-gradient(135deg, #4f46e5, #7c3aed);
          opacity: 0.7;
          animation: vision-tech-pulse 2s infinite;
        }
        
        @keyframes vision-tech-pulse {
          0% {
            transform: scale(1);
            opacity: 0.7;
          }
          70% {
            transform: scale(1.3);
            opacity: 0;
          }
          100% {
            transform: scale(1.3);
            opacity: 0;
          }
        }
        
        .vision-tech-contact-header h1 {
          font-size: 2.75rem;
          font-weight: 800;
          color: #111827;
          margin-bottom: 1rem;
          letter-spacing: -0.025em;
        }
        
        .vision-tech-contact-header h1 span {
          background: linear-gradient(90deg, #4f46e5, #7c3aed, #10b981);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          position: relative;
          z-index: 1;
        }
        
        .vision-tech-header-text {
          font-size: 1.125rem;
          color: #4b5563;
          max-width: 700px;
          margin: 0 auto;
          line-height: 1.6;
        }
        
        .vision-tech-contact-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.5rem;
          width: 100%;
        }
        
        @media (min-width: 992px) {
          .vision-tech-contact-grid {
            grid-template-columns: minmax(400px, 600px) 1fr;
          }
        }
        
        .vision-tech-form-card {
          background: white;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
          position: relative;
          border: 1px solid rgba(0, 0, 0, 0.05);
        }
        
        .vision-tech-card-inner {
          padding: 2rem;
        }
        
        .vision-tech-form-card h2 {
          font-size: 1.5rem;
          font-weight: 700;
          color: #111827;
          margin: 0 0 1rem 0;
        }
        
        .vision-tech-divider {
          height: 4px;
          width: 60px;
          background: linear-gradient(90deg, #4f46e5, #10b981);
          margin-bottom: 1.5rem;
          border-radius: 2px;
        }
        
        .vision-tech-form-message {
          padding: 1rem;
          border-radius: 8px;
          margin-bottom: 1.5rem;
          font-size: 0.95rem;
          display: flex;
          align-items: center;
        }
        
        .vision-tech-success {
          background: rgba(16, 185, 129, 0.1);
          color: #065f46;
          border: 1px solid rgba(16, 185, 129, 0.2);
        }
        
        .vision-tech-error {
          background: rgba(239, 68, 68, 0.1);
          color: #b91c1c;
          border: 1px solid rgba(239, 68, 68, 0.2);
        }
        
        .vision-tech-form {
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
        }
        
        .vision-tech-form-row {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.25rem;
        }
        
        @media (min-width: 576px) {
          .vision-tech-form-row {
            grid-template-columns: 1fr 1fr;
          }
        }
        
        .vision-tech-form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        
        .vision-tech-form-group label {
          font-size: 0.875rem;
          font-weight: 500;
          color: #4b5563;
        }
        
        .vision-tech-input-container {
          position: relative;
          border-radius: 8px;
          overflow: hidden;
        }
        
        .vision-tech-input-container input,
        .vision-tech-input-container select,
        .vision-tech-input-container textarea {
          width: 100%;
          padding: 0.75rem 1rem;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          font-size: 0.95rem;
          transition: all 0.2s ease;
          background-color: #f9fafb;
          color: #1f2937;
        }
        
        .vision-tech-input-container input:focus,
        .vision-tech-input-container select:focus,
        .vision-tech-input-container textarea:focus {
          outline: none;
          border-color: #4f46e5;
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
          background-color: white;
        }
        
        .vision-tech-input-container input::placeholder,
        .vision-tech-input-container textarea::placeholder {
          color: #9ca3af;
        }
        
        .vision-tech-textarea-container textarea {
          resize: vertical;
          min-height: 120px;
        }
        
        .vision-tech-submit-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          background: linear-gradient(135deg, #4f46e5, #7c3aed);
          color: white;
          font-weight: 600;
          font-size: 1rem;
          padding: 0.875rem 1.75rem;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
          position: relative;
          overflow: hidden;
          margin-top: 0.75rem;
        }
        
        .vision-tech-submit-btn::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(to right, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.15) 50%, rgba(255, 255, 255, 0) 100%);
          transform: translateX(-100%);
          z-index: 1;
        }
        
        .vision-tech-submit-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 15px rgba(79, 70, 229, 0.35);
        }
        
        .vision-tech-submit-btn:hover::before {
          animation: vision-tech-shine 1.5s infinite;
        }
        
        .vision-tech-submit-btn:disabled {
          background: #9ca3af;
          cursor: not-allowed;
          transform: none;
          box-shadow: none;
        }
        
        @keyframes vision-tech-shine {
          100% {
            transform: translateX(100%);
          }
        }
        
        .vision-tech-loading-spinner {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
        }
        
        .vision-tech-spinner-ring {
          border: 3px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          width: 100%;
          height: 100%;
          animation: vision-tech-spin 1s linear infinite;
        }
        
        @keyframes vision-tech-spin {
          to {
            transform: rotate(360deg);
          }
        }
        
        .vision-tech-contact-options {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 1.25rem;
        }
        
        .vision-tech-contact-card {
          background: white;
          border-radius: 12px;
          padding: 1.5rem;
          border: 1px solid rgba(0, 0, 0, 0.05);
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
          display: flex;
          flex-direction: column;
          gap: 1rem;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        
        .vision-tech-contact-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
        }
        
        .vision-tech-contact-card-header {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          color: #4f46e5;
        }
        
        .vision-tech-contact-card-header h3 {
          font-size: 1.125rem;
          font-weight: 600;
          color: #111827;
          margin: 0;
        }
        
        .vision-tech-contact-card p {
          font-size: 0.95rem;
          color: #4b5563;
          margin: 0;
          line-height: 1.5;
        }
        
        .vision-tech-action-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          background: #f3f4f6;
          color: #4f46e5;
          font-weight: 500;
          font-size: 0.95rem;
          padding: 0.75rem 1.25rem;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
          margin-top: auto;
        }
        
        .vision-tech-action-btn:hover {
          background: #e5e7eb;
        }
        
        .vision-tech-calendar-btn {
          background: rgba(79, 70, 229, 0.1);
          color: #4f46e5;
        }
        
        .vision-tech-calendar-btn:hover {
          background: rgba(79, 70, 229, 0.15);
        }
        
        .vision-tech-email-links {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }
        
        .vision-tech-email-link {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
          text-align: left;
          background: none;
          border: none;
          padding: 0.5rem 0;
          border-bottom: 1px solid #e5e7eb;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .vision-tech-email-link:last-child {
          border-bottom: none;
        }
        
        .vision-tech-email-link:hover {
          color: #4f46e5;
        }
        
        .vision-tech-email-link span:first-child {
          font-weight: 500;
          color: #4b5563;
        }
        
        .vision-tech-email-address {
          font-size: 0.9rem;
          color: #4f46e5;
        }
        
        .vision-tech-phone-list {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }
        
        .vision-tech-phone-item {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }
        
        .vision-tech-region {
          font-size: 0.9rem;
          font-weight: 500;
          color: #4b5563;
        }
        
        .vision-tech-phone-number {
          font-size: 1rem;
          color: #1f2937;
          font-weight: 600;
        }
        
        .vision-tech-hours {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }
        
        .vision-tech-hours-item {
          display: flex;
          justify-content: space-between;
          font-size: 0.95rem;
        }
        
        .vision-tech-hours-day {
          font-weight: 500;
          color: #4b5563;
        }
        
        .vision-tech-hours-time {
          color: #1f2937;
        }
        
        .vision-tech-locations {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
        }
        
        .vision-tech-location {
          background: #f3f4f6;
          border-radius: 20px;
          padding: 0.35rem 0.75rem;
          font-size: 0.85rem;
          color: #4b5563;
          transition: all 0.2s ease;
        }
        
        .vision-tech-location:hover {
          background: rgba(79, 70, 229, 0.1);
          color: #4f46e5;
        }
        
        @media (max-width: 768px) {
          .vision-tech-contact-header h1 {
            font-size: 2rem;
          }
          
          .vision-tech-header-text {
            font-size: 1rem;
          }
          
          .vision-tech-card-inner {
            padding: 1.5rem;
          }
        }
      `}</style>
    </div>
  );
};

export default VisionTechContactPage;